import type React from "react";
import { useState, useRef, useEffect } from "react";
import { Filter, ChevronDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { PartnerSuggestion } from "../../types";
import type {
  ContainerOrderFilters,
  ContainerType,
} from "../../types/containerOrders";
import { CONTAINER_TYPE_OPTIONS } from "../../constants/containerOrderConstants";
import CompanyFilter from "../reportanalytics/CompanyFilter";

/**
 * The list's filter panel — one dropdown holding every facet, the same shape
 * the shipments list uses.
 *
 * What it offers is deliberately narrower than the shipment panel, because an
 * order holds less: there is no vehicle on an order (trucks are assigned to the
 * generated shipments, not to their parent), and no pickup/delivery pair — an
 * order's route is two terminals, matched by one field. What it adds is what
 * only an order has: container type, the cut-off deadline, and whether the
 * order has generated anything yet.
 */

/** Which multi-select a checkbox belongs to. */
export type ContainerOrderFacet = "directions" | "containerTypes";

interface ContainerOrderFiltersProps {
  filters: ContainerOrderFilters;
  selectedClients: PartnerSuggestion[];
  selectedTerminals: PartnerSuggestion[];
  /** Number of filters currently applied — drives the button badge. */
  activeCount: number;
  onChange: (patch: Partial<ContainerOrderFilters>) => void;
  onToggle: (facet: ContainerOrderFacet, value: string) => void;
  onClientSelect: (client: PartnerSuggestion) => void;
  onClientRemove: (clientId: string) => void;
  onTerminalSelect: (terminal: PartnerSuggestion) => void;
  onTerminalRemove: (terminalId: string) => void;
  onClearFilters: () => void;
}

/** Collapsible section, so the panel opens short and expands where it matters. */
const FilterSection: React.FC<{
  title: string;
  defaultOpen?: boolean;
  badge?: number;
  children: React.ReactNode;
}> = ({ title, defaultOpen = false, badge, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-[rgba(11,15,14,0.08)] last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between py-2.5 text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-secondary-600">
            {title}
          </span>
          {!!badge && (
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          className={`h-3.5 w-3.5 text-mist transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {isOpen && <div className="pb-3">{children}</div>}
    </div>
  );
};

/** One checkbox in a multi-select facet. */
const CheckOption: React.FC<{
  label: string;
  checked: boolean;
  onChange: () => void;
}> = ({ label, checked, onChange }) => (
  <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-bone">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="h-3.5 w-3.5 shrink-0 accent-primary"
    />
    <span className="text-[11px] leading-none text-gray-700">{label}</span>
  </label>
);

export const ContainerOrderFiltersComponent: React.FC<
  ContainerOrderFiltersProps
> = ({
  filters,
  selectedClients,
  selectedTerminals,
  activeCount,
  onChange,
  onToggle,
  onClientSelect,
  onClientRemove,
  onTerminalSelect,
  onTerminalRemove,
  onClearFilters,
}) => {
  const { t } = useTranslation("containerOrders");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const containerTypes = filters.containerTypes ?? [];
  const cutOffFrom = filters.cutOffFrom ?? "";
  const cutOffTo = filters.cutOffTo ?? "";
  const dateCount = cutOffFrom || cutOffTo ? 1 : 0;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const dateInput =
    "w-full rounded-md border border-[rgba(11,15,14,0.15)] px-2 py-1.5 text-[11px] focus:border-transparent focus:ring-2 focus:ring-primary";

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 rounded-md border border-[rgba(11,15,14,0.15)] bg-white px-3 py-1.5 text-[12px] font-medium text-secondary-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
      >
        <Filter className="h-3.5 w-3.5" />
        <span>{t("filters.filters")}</span>
        {activeCount > 0 && (
          <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
            {activeCount}
          </span>
        )}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Below `sm` the sheet covers the page, so it gets a scrim: it makes the
          panel read as a layer over the list, and gives a large target to
          dismiss it with. */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 sm:hidden"
          onClick={() => setIsOpen(false)}
          aria-hidden
        />
      )}

      {isOpen && (
        /* Two presentations of one panel.

           From `sm` up it is a dropdown hanging off the button's right edge.
           Below that it stops being anchored to the button at all and becomes a
           bottom sheet pinned to the viewport — the toolbar wraps at narrow
           widths, so the button can sit anywhere on its row, and anchoring to
           it pushed the panel off one edge or the other. Pinned to the viewport
           there is nothing left to overflow.

           Either way it is a column with a capped height: the header and footer
           stay put and only the facets scroll, however many sections are open. */
        <div className="absolute left-0 right-auto z-50 mt-2 flex max-h-[calc(100vh-7rem)] w-[calc(100vw-2rem)] max-w-80 flex-col overflow-hidden rounded-lg border border-[rgba(11,15,14,0.15)] bg-white shadow-xl max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:mt-0 max-sm:max-h-[85vh] max-sm:w-auto max-sm:max-w-none max-sm:rounded-b-none max-sm:border-x-0 sm:left-auto sm:right-0 sm:w-80">
          <div className="flex shrink-0 items-center justify-between border-b border-[rgba(11,15,14,0.08)] bg-bone px-3 py-2">
            <span className="text-[12px] font-bold text-ink">
              {t("filters.filters")}
            </span>
            <div className="flex items-center gap-2">
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={onClearFilters}
                  className="text-[11px] font-medium text-signal transition-colors hover:underline"
                >
                  {t("filters.clearAll")}
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label={t("filters.close")}
                className="text-mist transition-colors hover:text-ink"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="dropdown-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-1">
            {/* Cut-off — the deadline the order is planned against, and the
                only date the list shows. */}
            <FilterSection
              title={t("filters.cutOff")}
              defaultOpen={dateCount > 0}
              badge={dateCount}
            >
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label
                    className="mb-1 block text-[11px] text-gray-500"
                    htmlFor="cutOffFrom"
                  >
                    {t("filters.from")}
                  </label>
                  <input
                    type="date"
                    id="cutOffFrom"
                    value={cutOffFrom}
                    max={cutOffTo || undefined}
                    onChange={(e) => onChange({ cutOffFrom: e.target.value })}
                    className={dateInput}
                  />
                </div>
                <div>
                  <label
                    className="mb-1 block text-[11px] text-gray-500"
                    htmlFor="cutOffTo"
                  >
                    {t("filters.to")}
                  </label>
                  <input
                    type="date"
                    id="cutOffTo"
                    value={cutOffTo}
                    min={cutOffFrom || undefined}
                    onChange={(e) => onChange({ cutOffTo: e.target.value })}
                    className={dateInput}
                  />
                </div>
              </div>
              {dateCount > 0 && (
                <button
                  type="button"
                  onClick={() => onChange({ cutOffFrom: "", cutOffTo: "" })}
                  className="mt-1.5 text-[11px] text-mist transition-colors hover:text-ink"
                >
                  {t("filters.clearDates")}
                </button>
              )}
            </FilterSection>

            {/* Codes only — the full labels ("20ft Dry Cargo") belong on the
                form where the type is chosen, not in a two-column checklist. */}
            <FilterSection
              title={t("filters.containerType")}
              defaultOpen={containerTypes.length > 0}
              badge={containerTypes.length}
            >
              <div className="grid grid-cols-3 gap-1">
                {CONTAINER_TYPE_OPTIONS.map((option) => (
                  <CheckOption
                    key={option.value}
                    label={option.value}
                    checked={containerTypes.includes(
                      option.value as ContainerType,
                    )}
                    onChange={() => onToggle("containerTypes", option.value)}
                  />
                ))}
              </div>
            </FilterSection>

            <FilterSection
              title={t("filters.client")}
              defaultOpen={selectedClients.length > 0}
              badge={selectedClients.length}
            >
              <CompanyFilter
                selectedCompanies={selectedClients}
                onCompanySelect={onClientSelect}
                onCompanyRemove={onClientRemove}
                placeholder={t("filters.searchClients")}
                className="relative"
              />
            </FilterSection>

            {/* One field for both ends of the route: an order's terminals are
                the same directory, and operators look for "was this order at
                Muuga?" rather than for a specific leg. */}
            <FilterSection
              title={t("filters.terminals")}
              defaultOpen={selectedTerminals.length > 0}
              badge={selectedTerminals.length}
            >
              <CompanyFilter
                selectedCompanies={selectedTerminals}
                onCompanySelect={onTerminalSelect}
                onCompanyRemove={onTerminalRemove}
                addresses
                placeholder={t("filters.searchTerminals")}
                className="relative"
              />
            </FilterSection>

            <FilterSection
              title={t("filters.options")}
              defaultOpen={!!filters.withoutLines}
              badge={filters.withoutLines ? 1 : 0}
            >
              <CheckOption
                label={t("filters.withoutLines")}
                checked={!!filters.withoutLines}
                onChange={() =>
                  onChange({ withoutLines: !filters.withoutLines })
                }
              />
            </FilterSection>
          </div>

          <div className="shrink-0 border-t border-[rgba(11,15,14,0.08)] bg-bone px-3 py-2">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full rounded-md bg-primary py-2 text-[12px] font-medium text-white transition-colors hover:bg-primary-700"
            >
              {t("filters.apply")}
              {activeCount > 0 && ` (${activeCount})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContainerOrderFiltersComponent;
